import React, { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useLang } from '../lib/i18n';
import styles from './Auth.module.css';
import GlobeLogo from '../components/GlobeLogo';

export default function Register() {
  const [fullName, setFullName] = useState('');
  const [nickname, setNickname] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { lang, t } = useLang();

  const passwordStrength = () => {
    if (password.length === 0) return null;
    if (password.length < 6) return 'weak';
    if (password.length < 10 || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) return 'fair';
    return 'strong';
  };

  const strength = passwordStrength();

  async function handleRegister(e) {
    e.preventDefault();
    setError('');
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
      setError('Password must include at least one letter and one number.');
      return;
    }
    setLoading(true);
    const { error: err } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName, nickname: nickname || fullName.split(' ')[0], phone } },
    });
    setLoading(false);
    if (err) { setError(err.message); return; }
    const redirect = searchParams.get('redirect');
    navigate(redirect ? `/login-success?redirect=${redirect}` : '/login-success');
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>

        {/* Left panel */}
        <div className={styles.brandPanel}>
          <div className={styles.brandLogo}>
            <GlobeLogo size={40} />
            <span className={styles.brandName}>Juzgo</span>
          </div>
          <blockquote className={styles.brandQuote}>
            "Five free itinerary searches when you join."
          </blockquote>
          <div className={styles.brandTag}>Juzgo · new members</div>
        </div>

        {/* Right panel — form */}
        <div className={styles.formPanel}>
          <div className={styles.tabs}>
            <Link to="/login" className={styles.tab}>Log in</Link>
            <span className={`${styles.tab} ${styles.tabActive}`}>Register</span>
          </div>

          <h2 className={styles.formH2}>Create your account.</h2>
          <p className={styles.formSub}>Start exploring 190+ destinations today.</p>

          <form onSubmit={handleRegister} className={styles.form}>
            <label className={styles.label}>Full name</label>
            <input
              type="text"
              placeholder="John Smith"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={styles.input}
              required
            />

            <label className={styles.label}>What should we call you? <span className={styles.optional}>(optional)</span></label>
            <input
              type="text"
              placeholder="e.g. Dave, YK, Traveller"
              value={nickname}
              onChange={(e) => setNickname(e.target.value)}
              className={styles.input}
            />

            <label className={styles.label}>Email</label>
            <input
              type="email"
              placeholder="your@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={styles.input}
              required
            />

            <label className={styles.label}>Phone <span className={styles.optional}>(optional)</span></label>
            <input
              type="tel"
              placeholder="+65 9000 0000"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className={styles.input}
            />

            <label className={styles.label}>Password</label>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={styles.input}
              required
            />
            {strength && (
              <div className={styles.strengthWrap}>
                <div className={`${styles.strengthBar} ${styles[`strength_${strength}`]}`} />
                <span className={styles.strengthText}>{strength.charAt(0).toUpperCase() + strength.slice(1)}</span>
              </div>
            )}

            {error && <div className={styles.error}>{error}</div>}

            <button type="submit" className={styles.btnPrimary} disabled={loading}>
              {loading ? 'Creating account…' : 'Create account →'}
            </button>
          </form>

          <p className={styles.switchText}>
            Already have an account? <Link to="/login" className={styles.switchLink}>Log in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
